import api from '../../services/api';
import { toast } from 'sonner';
import { Building2, AlertTriangle } from 'lucide-react';
import { EditableCell } from './EditableCell';
import { SpellersNotesCell } from './SpellersNotesCell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAMPAIGN_COLORS = {
  'Order Won': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'Trading': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'Quoting': 'bg-blue-100 text-blue-700 border-blue-300',
  'No Opportunity': 'bg-gray-200 text-gray-600 border-gray-300',
  __default: 'bg-orange-100 text-orange-700 border-orange-300',
};

const PRIORITY_COLORS = {
  'High': 'bg-red-100 text-red-700 border-red-300',
  'Medium': 'bg-amber-100 text-amber-800 border-amber-300',
  'Low': 'bg-gray-200 text-gray-600 border-gray-300',
  __default: 'bg-slate-100 text-slate-700 border-slate-300',
};

/** Spreadsheet-style list of tracked Contractor accounts (competitor trades). */
export const SpellersContractorsList = ({ accounts, onUpdated, onOpenAccount, onLinkCrm }) => {
  const saveField = async (account, field, value) => {
    try {
      const res = await api.put(`${API}/spellers/accounts/${account.id}`, { [field]: value });
      onUpdated?.(res.data.account);
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  if (accounts.length === 0) {
    return <div className="text-sm text-gray-400 text-center py-12">No Contractors tracked yet</div>;
  }

  return (
    <div className="overflow-x-auto rounded-2xl border-2 border-slate-300 shadow-lg bg-white" data-testid="spellers-contractors-list">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-800 text-left text-xs font-bold text-white uppercase tracking-wide">
            <th className="px-3 py-3 min-w-[220px]">Company</th>
            <th className="px-3 py-3 min-w-[160px]">Trade</th>
            <th className="px-3 py-3 min-w-[140px]">Account Owner</th>
            <th className="px-3 py-3 min-w-[150px]">Campaign Status</th>
            <th className="px-3 py-3 min-w-[160px]">Contact</th>
            <th className="px-3 py-3 min-w-[90px]">Priority</th>
            <th className="px-3 py-3 min-w-[110px]">Due Date</th>
            <th className="px-3 py-3 min-w-[200px]">Notes</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a, idx) => (
            <tr key={a.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-100'} border-t-2 border-slate-300 hover:bg-orange-50 transition-colors`} data-testid={`spellers-account-row-${a.id}`}>
              <td className="px-3 py-2.5">
                <button
                  onClick={() => (a.zoho_account_id ? onOpenAccount(a) : onLinkCrm(a))}
                  className="flex items-center gap-1.5 text-left font-bold text-slate-900 text-[13px] hover:text-orange-600 transition-colors"
                  data-testid={`spellers-account-open-${a.id}`}
                >
                  <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  {a.company}
                </button>
                {!a.zoho_account_id && (
                  <span
                    className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5"
                    data-testid={`spellers-account-not-in-crm-${a.id}`}
                  >
                    <AlertTriangle className="w-2.5 h-2.5" /> Not in CRM
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5"><EditableCell value={a.trade} onSave={(v) => saveField(a, 'trade', v)} testId={`spellers-account-trade-${a.id}`} /></td>
              <td className="px-3 py-2.5"><EditableCell value={a.account_owner} onSave={(v) => saveField(a, 'account_owner', v)} testId={`spellers-account-owner-${a.id}`} /></td>
              <td className="px-3 py-2.5"><EditableCell value={a.campaign_status} onSave={(v) => saveField(a, 'campaign_status', v)} colorMap={CAMPAIGN_COLORS} testId={`spellers-account-status-${a.id}`} /></td>
              <td className="px-3 py-2.5">
                <div className="space-y-0.5">
                  <EditableCell value={a.contact_name} onSave={(v) => saveField(a, 'contact_name', v)} placeholder="Name" testId={`spellers-account-contact-${a.id}`} />
                  <EditableCell value={a.telephone} onSave={(v) => saveField(a, 'telephone', v)} placeholder="Phone" testId={`spellers-account-phone-${a.id}`} />
                  <EditableCell value={a.email} onSave={(v) => saveField(a, 'email', v)} placeholder="Email" testId={`spellers-account-email-${a.id}`} />
                </div>
              </td>
              <td className="px-3 py-2.5"><EditableCell value={a.priority} onSave={(v) => saveField(a, 'priority', v)} colorMap={PRIORITY_COLORS} testId={`spellers-account-priority-${a.id}`} /></td>
              <td className="px-3 py-2.5"><EditableCell value={a.due_date} onSave={(v) => saveField(a, 'due_date', v)} type="date" testId={`spellers-account-due-${a.id}`} /></td>
              <td className="px-3 py-2.5">
                <SpellersNotesCell zohoId={a.zoho_account_id} kind="account" initialNote={a.crm_latest_note} testId={`spellers-account-notes-${a.id}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SpellersContractorsList;
